<template lang="pug">
  .v-input(:class="classes")
    input.v-input__field(
      required
      :type="type"
      v-focus="focus"
      :min="min"
      :max="max"
      :step="step"
      :value="value"
      :disabled="disabled"
      :pattern="pattern"
      @input="onInput")
    label {{ label }}
    .v-input__bar
    .v-input__hint(v-if="hint") {{ hint }}
</template>

<script>
export default {
  name: 'VInput',

  props: {
    label: String,
    value: [String, Number],
    disabled: Boolean,
    type: {
      type: String,
      default: 'text'
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    pattern: {
      type: [String],
      default: null
    },
    float: {
      type: Boolean,
      default: false
    },
    precision: {
      type: Number,
      default: 2
    },
    invalid: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: null
    },
    isVisiblePopover: Boolean,
    focus: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    classes () {
      return {
        'v-input--invalid': this.invalid
      }
    },
    step () {
      return this.float ? `0.${'0'.repeat(this.precision - 1)}1` : '1'
    },
    minValue () {
      return this.min !== null ? Number(this.min) : null
    },
    maxValue () {
      return this.max !== null ? Number(this.max) : null
    }
  },

  methods: {
    onInput (e) {
      let value = e.target.value

      if (this.float) {
        // Remove everything except digits and first separator (dot or comma)
        value = value.replace(/^(\d*[.,]?\d{0,2}).*$/, '$1')

        if (value !== '') {
          // Check if integer part is at max
          const integerPart = parseInt(value.split(/[,.]/)[0])

          if (this.maxValue !== null && integerPart >= this.maxValue) {
            value = this.maxValue.toString()
          } else if (value.includes('.') || value.includes(',')) {
            // Handle decimals only if not at max value
            const parts = value.split(/[,.]/)
            if (parts[1] && this.precision) {
              parts[1] = parts[1].slice(0, this.precision)
              value = parts.join(value.includes('.') ? '.' : ',')
            }

            // Check final value against max
            const numValue = parseFloat(value.replace(',', '.'))
            if (!isNaN(numValue) && this.maxValue !== null && numValue >= this.maxValue) {
              value = this.maxValue.toString()
            }
          }
        }
      }
      e.target.value = value
      this.$emit('input', value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-input {
  position: relative;

  &__field {
    width: 100%;
    font-size: 1.5rem;
    padding: 1rem 0;
    height: 4rem;
    display: block;
    border: none;
    background: transparent;
    border-bottom: .1rem solid $color-gray-light-2;

    &:focus {
      outline: none;
    }

    &:focus ~ label,
    &:valid ~ label {
      top: -0.6rem;
      font-size: 1.3rem;
      color: $color-gray;
    }

    &:disabled ~ label {
      color: $color-gray-light;
    }

    &:focus ~ .v-input__bar:before,
    &:focus ~ .v-input__bar:after {
      width: 50%;
    }
  }

  &__bar {
    position: relative;

    &:before,
    &:after	{
      content: "";
      width: 0;
      height: .2rem; 
      bottom: .1rem; 
      position: absolute;
      background: $color-primary;
      transition: 0.2s ease all;
    }

    &:before {
      left: 50%;
    }

    &:after {
      right: 50%; 
    }
  }
  
  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
    padding-top: .3rem;
  }

  label {
    top: 1rem;
    color: $color-black; 
    font-size: 1.5rem;
    position: absolute;
    pointer-events: none;
    transition: 0.2s ease all;
  }

  &--invalid {
    label {
      color: $color-red !important;
    }
  }

  &--invalid &__field {
    color: $color-red !important;
    border-bottom: .2rem solid $color-red !important;
  }

  &--invalid &__bar {
    &:before,
    &:after {
      background-color: transparent !important;
    }
  }
}
</style>